import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const RenderConfiguration = ({ onStartRender, isRendering }) => {
  const [config, setConfig] = useState({
    format: 'mp4',
    resolution: '1080p',
    quality: 'high',
    style: 'title_cards'
  });

  const formatOptions = [
    { value: 'mp4', label: 'MP4 (Recommended)', description: 'Best compatibility, smaller file size' },
    { value: 'mov', label: 'MOV (QuickTime)', description: 'Professional editing, larger file size' },
    { value: 'avi', label: 'AVI (Legacy)', description: 'Older format, good compatibility' }
  ];

  const resolutionOptions = [
    { value: '720p', label: '720p HD', description: '1280×720 - Smaller file size' },
    { value: '1080p', label: '1080p Full HD', description: '1920×1080 - Standard quality' },
    { value: '4k', label: '4K Ultra HD', description: '3840×2160 - Premium quality' }
  ];

  const qualityOptions = [
    { value: 'low', label: 'Low Quality', description: 'Fast render, ~50MB' },
    { value: 'medium', label: 'Medium Quality', description: 'Balanced, ~150MB' },
    { value: 'high', label: 'High Quality', description: 'Best output, ~300MB' }
  ];

  const styleOptions = [
    { value: 'title_cards', label: 'Title Cards', description: 'Individual credit cards' },
    { value: 'end_crawl', label: 'End Crawl', description: 'Scrolling credits' },
    { value: 'both', label: 'Both Styles', description: 'Title cards + end crawl' }
  ];

  const handleConfigChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const getEstimatedSize = () => {
    const baseSize = config?.resolution === '4k' ? 500 : config?.resolution === '1080p' ? 200 : 100;
    const qualityMultiplier = config?.quality === 'high' ? 1.5 : config?.quality === 'medium' ? 1 : 0.5;
    const styleMultiplier = config?.style === 'both' ? 1.8 : 1;
    return Math.round(baseSize * qualityMultiplier * styleMultiplier);
  };

  const getEstimatedTime = () => {
    const baseTime = config?.resolution === '4k' ? 15 : config?.resolution === '1080p' ? 8 : 5;
    const qualityMultiplier = config?.quality === 'high' ? 1.3 : config?.quality === 'medium' ? 1 : 0.7;
    const styleMultiplier = config?.style === 'both' ? 1.5 : 1;
    return Math.round(baseTime * qualityMultiplier * styleMultiplier);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="Settings" size={20} className="text-primary" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Render Configuration</h2>
          <p className="text-sm text-muted-foreground">Configure your video output settings</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Select
          label="Output Format"
          options={formatOptions}
          value={config?.format}
          onChange={(value) => handleConfigChange('format', value)}
        />

        <Select
          label="Resolution"
          options={resolutionOptions}
          value={config?.resolution}
          onChange={(value) => handleConfigChange('resolution', value)}
        />

        <Select
          label="Quality Preset"
          options={qualityOptions}
          value={config?.quality}
          onChange={(value) => handleConfigChange('quality', value)}
        />

        <Select
          label="Credit Style"
          options={styleOptions}
          value={config?.style}
          onChange={(value) => handleConfigChange('style', value)}
        />
      </div>
      <div className="bg-muted/50 rounded-lg p-4 mb-6">
        <h3 className="text-sm font-medium text-foreground mb-3">Estimated Output</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center space-x-2">
            <Icon name="HardDrive" size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">File Size:</span>
            <span className="text-sm font-medium text-foreground">~{getEstimatedSize()}MB</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Clock" size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Render Time:</span>
            <span className="text-sm font-medium text-foreground">~{getEstimatedTime()} min</span>
          </div>
        </div>
      </div>
      <Button
        variant="default"
        size="lg"
        fullWidth
        loading={isRendering}
        iconName="Play"
        iconPosition="left"
        onClick={() => onStartRender(config)}
        disabled={isRendering}
      >
        {isRendering ? 'Starting Render...' : 'Generate Video'}
      </Button>
    </div>
  );
};

export default RenderConfiguration;